import { message } from 'antd';
import { useState } from 'react';
import {
  StyledConnectGrid,
  StyledDarkButton,
  StyledInstructions,
  StyledQrCard,
  StyledSecretPanel,
} from './index.styled';

interface Props {
  /** Server-rendered data URI — the secret never reaches the browser as a string. */
  qrDataUrl?: string;
  /** Grouped in fours for reading aloud; copied without the spaces. */
  secret?: string;
  /** Shown in step 3 so the user can tell this entry from another Nxsys one. */
  email?: string;
}

/**
 * "Point your authenticator at this" — the QR, the three steps, and the manual key
 * behind a disclosure (NX-4996).
 *
 * Shared on purpose. Enrolment and "move to a new device" ask the user to do the
 * identical thing, and they had drifted into two answers: the wizard had numbered
 * steps, a shadowed QR card and a Copy button, while the settings screen had a bare
 * `<img>` and the key as plain text. Thang asked for one of them (14/08), and one
 * component is the only version of that which stays true.
 *
 * Deliberately just the panel: the heading, the copy around it and whatever comes
 * next are the caller's, because those genuinely differ — enrolment moves to a
 * separate step for the code, while the settings flow puts the field underneath.
 */
const ScanPanel = ({ qrDataUrl, secret, email }: Props) => {
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText((secret || '').replace(/\s/g, ''));
      setCopied(true);
    } catch {
      message.error(
        'Copying is blocked in this browser. Select the text instead.',
      );
    }
  };

  return (
    <StyledConnectGrid>
      <StyledQrCard>
        {qrDataUrl ? (
          <img src={qrDataUrl} alt="Two-factor QR code" />
        ) : (
          // Holds the space while the QR is on its way, so the panel does not
          // jump once it lands.
          <div style={{ width: 180, height: 180 }} />
        )}
      </StyledQrCard>
      <div>
        <StyledInstructions>
          <li>
            <span className="n">1</span>
            <span>Open your preferred 2FA app and choose “Add account”.</span>
          </li>
          <li>
            <span className="n">2</span>
            <span>Point the camera at the code on the left.</span>
          </li>
          <li>
            <span className="n">3</span>
            <span>
              Check the entry reads Nxsys{email ? ` · ${email}` : ''}.
            </span>
          </li>
        </StyledInstructions>

        <StyledSecretPanel>
          <button
            type="button"
            className="head"
            onClick={() => setShowSecret((v) => !v)}
          >
            Can&apos;t scan? Enter the key manually
            <span>{showSecret ? '⌃' : '⌄'}</span>
          </button>
          {showSecret && (
            <div className="body">
              <div className="label">Setup key · time-based · 30s interval</div>
              <div className="row">
                <code>{secret}</code>
                <StyledDarkButton type="button" onClick={copyKey}>
                  {copied ? 'Copied' : 'Copy'}
                </StyledDarkButton>
              </div>
            </div>
          )}
        </StyledSecretPanel>
      </div>
    </StyledConnectGrid>
  );
};

export default ScanPanel;
